import { useEffect, useState } from 'react'
import { api, unlock, type Child, type Overview, type Progress as ProgressData, type ReportSettings, type BillingStatus } from '../api'
import { DevicesPanel } from '../components/DeviceCode'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Field, Heading, Page, inputStyle } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { WordRequests, useWaitingCount } from '../components/WordRequests'
import { ReadingList } from '../components/ReadingList'
import { Trace, useTrace } from '../lib/trace'
import { Shell } from '../components/Shell'
import { AvatarPicker } from '../components/AvatarPicker'
import { BarChart, TrendChart } from '../components/MiniCharts'
import { TreeDrawing } from './Tree'
import { GrownUpGate } from '../components/GrownUpGate'
import { ChildPinMaker } from '../components/ChildPinMaker'
import { LanguageCard } from '../components/LanguageCard'
import { tryStore } from '../lib/tryStore'
// `tr`, not `t`: the Progress tab already names its tiles `t`.
import { dateLocale, fromServer, screenLanguage, t as tr } from '../lib/i18n'


type Tab = 'children' | 'words' | 'books' | 'progress' | 'recent' | 'account'

/**
 * Settings, behind the grown-up PIN (D-065, 8 Oct 2026).
 *
 * It used to sit behind a birth-year question, "a speed bump, not a
 * password". That stopped being enough once Settings could reset a child's
 * PIN: any child who knows how old their parents are could answer it and
 * then open a sibling's practice. The year is still asked once, the first
 * time, before the grown-up PIN is made.
 *
 * Leaving Settings drops the grown-up's ticket (App.refresh), so it does not
 * stay behind on the tablet for whoever picks it up next.
 */
/** `device`: this tablet's name when it is a child's device (DeviceCode.tsx). */
export function Settings({ onHome, device = null, onStartingCheck }: {
  onHome: () => void
  device?: string | null
  /** Hand the tablet to a child for the starting-point check (StartingCheck.tsx). */
  onStartingCheck?: (child: Child) => void
}) {
  const [passed, setPassed] = useState(false)
  if (!passed) {
    return (
      <Page width={420}>
        <GrownUpGate why={tr('To open Settings.')} onPass={() => setPassed(true)} onCancel={onHome} />
      </Page>
    )
  }
  return <SettingsBody onHome={onHome} device={device} onStartingCheck={onStartingCheck} />
}

function SettingsBody({ onHome, device, onStartingCheck }: {
  onHome: () => void; device: string | null; onStartingCheck?: (child: Child) => void
}) {
  const [tab, setTab] = useState<Tab>('children')
  // A language change re-draws every tab: t() reads the language as it renders.
  const [, setLanguage] = useState(screenLanguage())
  const [children, setChildren] = useState<Child[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = () => {
    setError(null)
    api.children().then((r) => setChildren(r.children)).catch(setError)
  }
  useEffect(load, [])

  // The number on the tab is the whole point of the tab. A queue a parent
  // has to remember to visit is a queue that fills up, and a full queue is
  // how a child learns that asking does nothing.
  //
  // This call MUST stay above the two returns below. It was underneath them,
  // which meant the first render (children still loading) ran two hooks and
  // the second ran three. React refuses that and throws out the whole
  // screen, so Settings rendered nothing at all and the Words tab did not
  // exist — which is how Sam came to report a missing button rather than a
  // white page. Every hook, every render, no exceptions.
  const waiting = useWaitingCount()

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} grownUp />
  if (!children) return <Loading grownUp />

  const tabs: [Tab, string][] = [
    ['children', tr('Children')],
    // "Word requests", not "Words" (Sam, 8 Oct: unclear without opening it).
    ['words', waiting ? `${tr('Word requests')} · ${waiting}` : tr('Word requests')],
    ['books', tr('Books')],
    ['progress', tr('Progress')],
    ['recent', tr('Recent learning')],
    ['account', tr('Your account')],
  ]

  return (
    <Page width={620}>
      <Shell exitMode="home" onExit={onHome} grownUp>

        <Heading>{tr('Settings')}</Heading>

        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {tabs.map(([key, label]) => {
            const on = tab === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                style={{
                  fontFamily: 'inherit', fontSize: 15, fontWeight: on ? 700 : 600,
                  color: on ? theme.blue : theme.muted,
                  background: on ? theme.blueFaint : 'transparent',
                  border: `1.5px solid ${on ? theme.blueTint : 'transparent'}`,
                  borderRadius: radius.sm, padding: '9px 14px',
                  minHeight: TAP_MIN, cursor: 'pointer',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>

        {tab === 'children' ? <ChildrenTab children={children} onChanged={load} onStartingCheck={onStartingCheck} /> : null}
        {tab === 'words' ? <WordRequests children={children} /> : null}
        {tab === 'books' ? <ReadingList children={children} /> : null}
        {tab === 'progress' ? <ProgressTab children={children} onDetail={() => setTab('recent')} /> : null}
        {tab === 'recent' ? <RecentLearningTab children={children} /> : null}
        {tab === 'account' ? <AccountTab device={device} onLanguage={() => setLanguage(screenLanguage())} /> : null}
      </Shell>
    </Page>
  )
}

function ChildrenTab({
  children, onChanged, onStartingCheck,
}: { children: Child[]; onChanged: () => void; onStartingCheck?: (child: Child) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TrialCarryOver children={children} />
      {children.map((child) => (
        <ChildEditor key={child.learner_id} child={child} onChanged={onChanged} onStartingCheck={onStartingCheck} />
      ))}
      <p style={{ fontSize: 14, color: theme.faint, lineHeight: 1.55, margin: 0 }}>
        {tr('Archiving hides a child from Home and keeps every answer they have ever given. There is no delete, on purpose — three years of practice is not something an app should make easy to throw away.')}
      </p>
    </div>
  )
}

const fmtOffset = (n: number) => (n > 0 ? `+${n}` : String(n))

function ChildEditor({ child, onChanged, onStartingCheck }: {
  child: Child; onChanged: () => void; onStartingCheck?: (child: Child) => void
}) {
  const [offset, setOffset] = useState(child.year_offset)
  const [year, setYear] = useState(child.school_year)
  const [saved, setSaved] = useState(false)
  const [current, setCurrent] = useState(child)
  // A refused save used to show nothing at all (Codex, round 14).
  const [problem, setProblem] = useState<string | null>(null)

  const save = async () => {
    setProblem(null)
    try {
      await api.updateChild(child.learner_id, { school_year: year, year_offset: offset })
    } catch (err) {
      setProblem(fromServer(err, 'That did not save.'))
      return
    }
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1500)
    onChanged()
  }

  // Recent first-try accuracy (last 14 days, at least 10 answers, else null).
  const [accuracy, setAccuracy] = useState<number | null>(null)
  useEffect(() => {
    api.progress(child.learner_id)
      .then((p) => setAccuracy(p.recent_accuracy ?? null))
      .catch(() => setAccuracy(null))
  }, [child.learner_id])
  const suggest = accuracy === null ? 0
    : accuracy > 0.9 && child.year_offset < 3 ? 1
      : accuracy < 0.7 && child.year_offset > -3 ? -1 : 0
  const applySuggestion = async () => {
    const next = child.year_offset + suggest
    setProblem(null)
    try {
      await api.updateChild(child.learner_id, { school_year: child.school_year, year_offset: next })
    } catch (err) {
      setProblem(fromServer(err, 'That did not save.'))
      return
    }
    setOffset(next)
    onChanged()
  }

  return (
    <Card>
      <div style={{ fontSize: 18, fontWeight: 800, color: theme.text, marginBottom: 14 }}>
        <Trace id="T2.display_name">{child.display_name}</Trace>
      </div>

      <div style={{ marginBottom: 20 }}>
        <AvatarPicker
          child={current}
          onChanged={(updated) => { setCurrent(updated); onChanged() }}
        />
      </div>

      {/* Side by side, small boxes (Sam, 8 Oct: a whole row each was too
          much room for one number). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 160px))', gap: 14 }}>
        <Field label={tr('School year')}>
          <input
            style={{ ...inputStyle }} type="number" min={0} max={13} value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </Field>
        <Field label={tr('Offset (−3 to +3)')}>
          <input
            style={{ ...inputStyle }} type="number" min={-3} max={3} value={offset}
            onChange={(e) => setOffset(Number(e.target.value))}
          />
        </Field>
      </div>
      <p style={{ margin: '-8px 0 16px', fontSize: 13, color: theme.faint }}>
        {tr('Raise the offset if the work feels too easy, lower it if too hard.')}
      </p>

      {/* A suggestion, never a change: the app aims for 85 to 90% right, and
          only a grown-up moves the level (build list item 9, 25 Oct 2026;
          research/how-it-works.md section 1, "How the app adapts"). */}
      {suggest && accuracy !== null ? (
        <div style={{
          margin: '-6px 0 16px', padding: '10px 12px', borderRadius: radius.sm,
          background: theme.bluePale, border: `1px solid ${theme.blue}`,
          fontSize: 14, color: theme.text, lineHeight: 1.5,
        }}>
          {screenLanguage() === 'zh-TW'
            ? <>{child.display_name} 最近兩週第一次作答的答對率是 <b>{Math.round(accuracy * 100)}%</b>，
                單字可能太{suggest > 0 ? '簡單' : '難'}了。</>
            : screenLanguage() === 'es'
            ? <>{child.display_name} acertó <b>{Math.round(accuracy * 100)}%</b> al primer intento en las
                últimas dos semanas, así que las palabras pueden ser demasiado {suggest > 0 ? 'fáciles' : 'difíciles'}.</>
            : <>{child.display_name} got <b>{Math.round(accuracy * 100)}%</b> right first time over
                the last two weeks, so the words may be too {suggest > 0 ? 'easy' : 'hard'}.</>}
          {' '}{tr('The app aims for 85 to 90%. New words will move to the new level over the next few practices.')}
          <div style={{ marginTop: 8 }}>
            <Button tone="quiet" onClick={() => void applySuggestion()}>
              {suggest > 0 ? tr('Raise the offset to') : tr('Lower the offset to')} {fmtOffset(child.year_offset + suggest)}
            </Button>
          </div>
        </div>
      ) : null}

      {onStartingCheck ? <StartingCheckCard child={child} onStart={() => onStartingCheck(child)} /> : null}

      <PinSetting child={child} />

      {problem ? <p style={{ margin: '0 0 10px', color: theme.red, fontSize: 14 }}>{problem}</p> : null}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button onClick={save}>{saved ? tr('Saved') : tr('Save')}</Button>
        <Button
          tone="ghost"
          onClick={async () => {
            // One tap used to hide a child with no way back in the app (audit).
            if (!window.confirm(tr('Archive {name}? Their words and answers are kept, but they leave the home screen.', { name: child.display_name }))) return
            await api.archiveChild(child.learner_id)
            onChanged()
          }}
        >
          {tr('Archive')}
        </Button>
      </div>
    </Card>
  )
}

/**
 * Set or reset a child's PIN. The new PIN is shown here once, and never
 * emailed (28 Oct 2026; the email only says that it changed). It cannot be
 * shown again, because only a scrambled copy is kept. A forgotten PIN is
 * replaced, never looked up.
 */
function PinSetting({ child }: { child: Child }) {
  const [shown, setShown] = useState<{ pin: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [choosing, setChoosing] = useState(false)
  const [chosen, setChosen] = useState(false)

  const make = async () => {
    setBusy(true)
    try {
      setShown(await api.setChildPin(child.learner_id))
      // Making a PIN also hands back the child's ticket; this is a grown-up's
      // screen, so do not start sending it.
      unlock.use(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      margin: '4px 0 18px', padding: 14, borderRadius: radius.md,
      background: theme.blueFaint, border: `1px solid ${theme.bluePale}`,
    }}>
      <div style={{ fontWeight: 800, color: theme.text, marginBottom: 4 }}>{tr('PIN')}</div>
      <p style={{ margin: '0 0 10px', fontSize: 14, color: theme.muted, lineHeight: 1.5 }}>
        {tr('Each child types their own PIN before practising.')}{' '}
        <b>{child.has_pin ? tr('A PIN is set.') : tr('No PIN yet.')}</b>
      </p>
      {shown ? (
        <div role="status" style={{ marginBottom: 10 }}>
          <div style={{
            fontFamily: "'Fredoka', sans-serif", fontSize: 40, fontWeight: 600,
            letterSpacing: '0.25em', color: theme.text,
          }}>{shown.pin}</div>
          <div style={{ fontSize: 14, color: theme.text2 }}>
            {tr("Write it down now — it can't be shown again. You can always set a new one.")}
          </div>
        </div>
      ) : null}
      {chosen ? (
        <p role="status" style={{ margin: '0 0 10px', fontSize: 15, color: theme.greenDark, fontWeight: 700 }}>
          {tr('{name} chose a new PIN.', { name: child.display_name })}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button tone="quiet" onClick={() => setChoosing(true)}>
          {tr('Help {name} choose a PIN', { name: child.display_name })}
        </Button>
        <Button tone="ghost" onClick={make} disabled={busy}>
          {tr('Make a random one')}
        </Button>
      </div>
      {choosing ? (
        <ChildPinMaker
          child={child}
          onCancel={() => setChoosing(false)}
          onDone={() => { setChoosing(false); setChosen(true); setShown(null); unlock.use(null) }}
        />
      ) : null}
    </div>
  )
}

/**
 * Progress, at a glance (Sam, 8 Oct 2026): this week or this month, a plain
 * sentence first, four numbers, two small charts and the few words that are
 * hard. "Communicate the progress or the difficulty, without overwhelming."
 * The word-by-word detail is in Recent learning.
 */
/**
 * How far through the current stage, 0 to 100. The stage and where it starts
 * come from the server (repo/games.py); a copy of the stage list lived here
 * until 22 Oct 2026, and would have gone wrong when the tree got 25 stages.
 */
const stagePart = (from: number, grown: number, next: number) =>
  Math.max(4, Math.min(100, ((grown - from) / Math.max(1, next - from)) * 100))

function ProgressTab({ children, onDetail }: { children: Child[]; onDetail: () => void }) {
  const [which, setWhich] = useState(children[0]?.learner_id ?? '')
  const [period, setPeriod] = useState<'week' | 'month'>('week')
  const [data, setData] = useState<Overview | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!which) return
    setData(null); setFailed(false)
    api.overview(which, period).then(setData).catch(() => setFailed(true))
  }, [which, period])

  if (!children.length) return <Card>{tr('No children yet.')}</Card>
  const span = period === 'week' ? 'week' : 'month'
  const t = data?.tiles

  const chip = (on: boolean, label: string, onClick: () => void) => (
    <button key={label} type="button" onClick={onClick} aria-pressed={on} style={{
      fontFamily: 'inherit', fontSize: 15, fontWeight: on ? 800 : 600, cursor: 'pointer',
      color: on ? '#fff' : theme.text2, background: on ? theme.blue : theme.card,
      border: `1.5px solid ${on ? theme.blue : theme.border}`, borderRadius: radius.pill,
      padding: '7px 14px', minHeight: 40,
    }}>{label}</button>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {children.map((c) => chip(which === c.learner_id, c.display_name, () => setWhich(c.learner_id)))}
        <span style={{ flex: 1 }} />
        {chip(period === 'week', tr('This week'), () => setPeriod('week'))}
        {chip(period === 'month', tr('This month'), () => setPeriod('month'))}
      </div>

      {failed ? <Card>{tr('That did not load. Try again in a moment.')}</Card> : !data || !t ? <Card>{tr('Loading…')}</Card> : (
        <>
          {/* The plain sentence first: most parents need only this. */}
          <Card style={{ background: theme.greenPale, borderColor: theme.greenPale }}>
            <div style={{ fontSize: 17, lineHeight: 1.5, color: theme.text }}>
              {/* Built from numbers, so each language has its own sentence:
                  Chinese puts the parts in a different order. */}
              {screenLanguage() === 'zh-TW' ? (
                t.minutes === 0
                  ? <>{span === 'week' ? '這週' : '這個月'}沒有練習。不會有任何損失：單字會等著，之後自己再出現。</>
                  : <>
                      <b>{data.display_name}</b> {span === 'week' ? '這週' : '這個月'}練習了 <b>{t.days_practised} 天</b>，
                      大約 <b>{t.minutes} 分鐘</b>
                      {t.words_grown ? <>，有 <b>{t.words_grown} 個字</b>完全學會</> : null}。
                      {data.tricky.length
                        ? <>有 <b>{data.tricky.length} 個字</b>比較難（見下方）。</>
                        : <>目前沒有特別難的字。</>}
                    </>
              ) : screenLanguage() === 'es' ? (
                t.minutes === 0
                  ? <>No hubo práctica {span === 'week' ? 'esta semana' : 'este mes'}. No se pierde nada: las palabras esperan y vuelven solas.</>
                  : <>
                      <b>{data.display_name}</b> practicó <b>{t.days_practised} día{t.days_practised === 1 ? '' : 's'}</b>{' '}
                      {span === 'week' ? 'esta semana' : 'este mes'}, unos <b>{t.minutes} minutos</b>
                      {t.words_grown ? <>, y <b>{t.words_grown} palabra{t.words_grown === 1 ? '' : 's'}</b> quedaron dominadas</> : null}.
                      {data.tricky.length
                        ? <> {data.tricky.length === 1 ? 'Una palabra está' : `${data.tricky.length} palabras están`} costando trabajo (abajo).</>
                        : <> Ninguna palabra está costando trabajo en especial.</>}
                    </>
              ) : t.minutes === 0
                ? <>No practice this {span}. Nothing is lost: words wait, and come back on their own.</>
                : <>
                    <b>{data.display_name}</b> practised on <b>{t.days_practised} day{t.days_practised === 1 ? '' : 's'}</b> this {span},
                    about <b>{t.minutes} minutes</b>
                    {t.words_grown ? <>, and <b>{t.words_grown} word{t.words_grown === 1 ? '' : 's'}</b> finished growing</> : null}.
                    {data.tricky.length
                      ? <> {data.tricky.length === 1 ? 'One word is' : `${data.tricky.length} words are`} proving tricky (below).</>
                      : <> Nothing is proving especially tricky.</>}
                  </>}
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            {([
              [tr('Minutes'), t.minutes, t.minutes_before],
              [tr('Days practised'), t.days_practised, t.days_before],
              [tr('Words grown'), t.words_grown, t.grown_before],
              [tr('New words met'), t.new_words, t.new_before],
            ] as [string, number, number][]).map(([label, now, before]) => (
              <Card key={label} style={{ padding: 14 }}>
                <div style={{ fontSize: 13, color: theme.muted, fontWeight: 700 }}>{label}</div>
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 30, fontWeight: 600, color: theme.text, lineHeight: 1.1 }}>
                  <Trace id="calc.counted from T14 trials (and T7 grown_at) for this period">{now}</Trace>
                </div>
                <div style={{ fontSize: 12, color: theme.faint }}>
                  {span === 'week' ? tr('last week: {n}', { n: before }) : tr('last month: {n}', { n: before })}
                </div>
              </Card>
            ))}
          </div>

          <Card>
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 8 }}>
              {period === 'week' ? tr('Minutes each day') : tr('Minutes each week')}
            </div>
            <BarChart unit={tr('min')} bars={data.bars.map((b) => ({ label: b.label, value: b.minutes }))} />
          </Card>

          <Card>
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 2 }}>
              {tr('Words fully grown, last 8 weeks')}
            </div>
            <div style={{ fontSize: 13, color: theme.muted, marginBottom: 8 }}>
              {tr('A word is grown once your child has got it right in all three kinds of question (knowing it, spelling it, using it), six right answers in all, over at least three practices. A grown word stays grown, so this only goes up.')}
            </div>
            <TrendChart unit={tr('words')} points={data.trend.map((p) => ({ label: p.label, value: p.grown }))} />
          </Card>

          <Card>
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 8 }}>{tr('Needs a little help')}</div>
            {data.tricky.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr style={{ color: theme.muted, fontSize: 13, textAlign: 'left' }}>
                    <th style={{ padding: '4px 4px', fontWeight: 700 }}>{tr('Word')}</th>
                    <th style={{ padding: '4px 4px', fontWeight: 700, textAlign: 'right' }}>{tr('Met')}</th>
                    <th style={{ padding: '4px 4px', fontWeight: 700, textAlign: 'right' }}>{tr('Right on their own')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tricky.map((w) => (
                    <tr key={w.word} style={{ borderTop: `1px solid ${theme.borderSoft}` }}>
                      <td style={{ padding: '7px 4px', fontWeight: 800, color: theme.text }}>
                        <Trace id="T3.display_form">{w.word}</Trace>
                      </td>
                      <td style={{ padding: '7px 4px', textAlign: 'right' }}>{w.met}</td>
                      <td style={{ padding: '7px 4px', textAlign: 'right' }}>{w.unaided}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 15, color: theme.muted }}>
                {span === 'week' ? tr('Nothing tricky this week.') : tr('Nothing tricky this month.')}
              </div>
            )}
            <div style={{ fontSize: 13, color: theme.faint, marginTop: 8 }}>
              {tr('These come back more often on their own. Using one at dinner helps too.')}
            </div>
          </Card>

          {/* The tree and the trophies, each in a card of its own (Sam, 8 Oct:
              two packed lines were too much at once). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
            <Card style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 14 }}>
              <div style={{ width: 84, flexShrink: 0 }}>
                <TreeDrawing stage={data.tree.stage ?? 0} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: theme.muted, fontWeight: 700 }}>{tr("{name}'s tree", { name: data.display_name })}</div>
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 21, fontWeight: 600, color: theme.greenDarker }}>
                  {data.tree.name}
                </div>
                <div style={{ fontSize: 13, color: theme.text2, margin: '2px 0 6px' }}>
                  <Trace id="calc.counted from T7.grown_at">
                    {data.tree.grown === 1 ? tr('1 word grown') : tr('{n} words grown', { n: data.tree.grown })}
                  </Trace>
                </div>
                {data.tree.next_at && data.tree.next_name ? (
                  <>
                    <div style={{ height: 8, borderRadius: 99, background: theme.sand, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 99, background: theme.green,
                        width: `${stagePart(data.tree.stage_at ?? 0, data.tree.grown, data.tree.next_at)}%`,
                      }} />
                    </div>
                    <div style={{ fontSize: 12, color: theme.muted, marginTop: 4 }}>
                      {tr('{n} more to {next}', { n: data.tree.next_at - data.tree.grown, next: data.tree.next_name.toLowerCase() })}
                    </div>
                  </>
                ) : null}
              </div>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 13, color: theme.muted, fontWeight: 700, marginBottom: 8 }}>
                {span === 'week' ? tr('New trophies this week') : tr('New trophies this month')}
              </div>
              {data.badges.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {data.badges.map((b) => (
                    <span key={b.name} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                      borderRadius: radius.pill, background: theme.orangePale, fontSize: 14,
                      fontWeight: 700, color: theme.text,
                    }}>
                      <span aria-hidden>{b.emoji}</span><Trace id="T18.name">{b.name}</Trace>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 14, color: theme.faint }}>
                  {span === 'week' ? tr('None this week.') : tr('None this month.')}
                </div>
              )}
            </Card>
          </div>
          <div>
            <Button tone="quiet" onClick={onDetail}>{tr('See every word in Recent learning')}</Button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Recent learning: the word-by-word detail, for a parent who wants it. This
 * was the whole Progress tab until 8 Oct 2026; it moved here so Progress can
 * stay at a glance. Adds this week's words: met for the first time, and
 * fully grown.
 */
function RecentLearningTab({ children }: { children: Child[] }) {
  const [which, setWhich] = useState(children[0]?.learner_id ?? '')
  const [week, setWeek] = useState<Overview | null>(null)
  useEffect(() => {
    if (which) api.overview(which, 'week').then(setWeek).catch(() => setWeek(null))
  }, [which])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <OldProgress children={children} which={which} setWhich={setWhich} />
      {week ? (
        <Card>
          <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 6 }}>{tr("This week's words")}</div>
          <p style={{ margin: '0 0 8px', fontSize: 15, color: theme.text2, lineHeight: 1.55 }}>
            <b>{tr('Fully grown:')}</b> {week.grown_words.length ? week.grown_words.join(', ') : tr('none yet this week')}.
          </p>
          <p style={{ margin: 0, fontSize: 15, color: theme.text2, lineHeight: 1.55 }}>
            <b>{tr('Met for the first time:')}</b> {week.new_words.length ? week.new_words.join(', ') : tr('none this week')}.
          </p>
          {week.rechecked ? (
            <p style={{ margin: '8px 0 0', fontSize: 15, color: theme.text2 }}>
              {week.rechecked === 1
                ? tr('Checked 1 word learned earlier: still known.')
                : tr('Checked {n} words learned earlier: still known.', { n: week.rechecked })}
            </p>
          ) : null}
        </Card>
      ) : null}
    </div>
  )
}

function OldProgress({
  children, which, setWhich,
}: { children: Child[]; which: string; setWhich: (id: string) => void }) {
  const [data, setData] = useState<ProgressData | null>(null)

  useEffect(() => {
    if (which) api.progress(which).then(setData).catch(() => setData(null))
  }, [which])

  if (!children.length) return <Card>{tr('No children yet.')}</Card>

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {children.map((c) => (
          <button
            key={c.learner_id}
            type="button"
            onClick={() => setWhich(c.learner_id)}
            style={{
              fontFamily: 'inherit', fontSize: 15,
              fontWeight: which === c.learner_id ? 700 : 600,
              color: theme.text2,
              background: which === c.learner_id ? theme.orangePale : '#FFFFFF',
              border: `1.5px solid ${which === c.learner_id ? theme.orange : theme.border}`,
              borderRadius: radius.sm, padding: '8px 14px',
              minHeight: TAP_MIN, cursor: 'pointer',
            }}
          >
            {c.display_name}
          </button>
        ))}
      </div>

      {!data ? <Card>{tr('Loading…')}</Card> : data.empty_message ? (
        // The server's one line, translated here (round 13, 28 Oct 2026).
        <Card style={{ textAlign: 'center', color: theme.muted }}>{tr(data.empty_message)}</Card>
      ) : (
        <Card>
          <Row label={tr('Words in the pool')} value={String(data.pool_size)}
               trace="calc.rows in T7 learner_word, not counting retired ones" />
          <Row label={tr('Questions answered')} value={String(data.totals?.trials ?? 0)}
               trace="calc.every T14 trial for this child, import included" />
          <Row label={tr('Day streak')} value={String(data.streak_days)}
               trace="calc.separate days with a finished T12 session" />
          {/* The one percentage in the app, and it is on the PARENT screen.
              D-048 keeps any number that can go backwards away from the
              child; a parent is the person it is actually useful to. */}
          <Row
            label={tr('Recent accuracy')}
            trace="calc.first-attempt T14 trials marked correct over the last 14 days"
            value={data.recent_accuracy === null
              ? tr('Not enough yet')
              : `${Math.round(data.recent_accuracy * 100)}%`}
          />
          <Row label={tr('First day')} value={String(data.totals?.first_day ?? '—')}
               trace="calc.earliest T14 event_date for this child" />

          {data.hardest_words.length ? (
            <>
              <div style={{
                fontSize: 15, fontWeight: 700, color: theme.text2,
                margin: '18px 0 8px',
              }}>
                {tr('Hardest words')}
              </div>
              {data.hardest_words.map((w) => (
                <Row key={w.word} label={w.word}
                     value={tr('missed {missed} of {seen}', { missed: w.missed, seen: w.seen })}
                     trace="calc.T8 lapses against T8 reps, worst first" />
              ))}
            </>
          ) : null}
        </Card>
      )}
    </div>
  )
}

/**
 * How often a progress email arrives: off, daily, weekly or monthly.
 * Off until chosen — email is opt-in. The preview shows exactly what the
 * next one would say, so a grown-up can judge it before switching it on,
 * and it works before any email service is connected.
 */
function ReportEmails() {
  const [s, setS] = useState<ReportSettings | null>(null)
  const [preview, setPreview] = useState<{ subject: string; text: string; html: string } | null>(null)

  useEffect(() => { api.reportSettings().then(setS).catch(() => undefined) }, [])
  if (!s) return null

  const choose = async (frequency: string) => {
    const saved = await api.setReportFrequency(frequency)
    setS({ ...s, frequency: saved.frequency as ReportSettings['frequency'] })
    setPreview(null)
  }
  const labels: Record<string, string> = { off: tr('Off'), daily: tr('Daily'), weekly: tr('Weekly'), monthly: tr('Monthly') }

  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {tr('Progress emails')}
      </div>
      <p style={{ margin: '0 0 12px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
        {tr('What each child practised, the words that grew, the ones still tricky (with exactly how tricky), their tree and trophies, and one word to ask them about. Weekly unless you change it. Sent to {email} on Monday mornings.',
            { email: s.email ?? tr('your email') })}
      </p>
      <Trace id="T20.value" writes block title="a setting row: scope parent, key report_frequency">
        <span style={{ fontSize: 13, color: theme.faint }}>{tr('How often:')}</span>
      </Trace>
      <div role="radiogroup" aria-label={tr('How often')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {s.choices.map((c) => {
          const on = s.frequency === c
          return (
            <button key={c} type="button" role="radio" aria-checked={on} onClick={() => void choose(c)}
              style={{
                minHeight: TAP_MIN, padding: '0 16px', borderRadius: radius.pill, cursor: 'pointer',
                fontWeight: 700, fontSize: 15,
                border: `1.5px solid ${on ? theme.blue : theme.border}`,
                background: on ? theme.blue : theme.card, color: on ? '#fff' : theme.text2,
              }}>{labels[c] ?? c}</button>
          )
        })}
      </div>
      {!s.email_ready && s.frequency !== 'off' ? (
        <p style={{ margin: '0 0 12px', fontSize: 14, color: theme.orangeDark, lineHeight: 1.5 }}>
          {tr("Saved. Email isn't connected yet, so nothing will be sent until it is — you can read what it would say with Preview.")}
        </p>
      ) : null}
      <Button tone="quiet" onClick={() => {
        void api.reportPreview(s.frequency === 'off' ? 'weekly' : s.frequency).then(setPreview)
      }}>{tr("See this week's email")}</Button>
      {preview ? (
        <div style={{ marginTop: 12, borderRadius: radius.md, background: theme.cardWarm,
                      border: `1px solid ${theme.borderWarm}`, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', fontSize: 14, color: theme.muted }}>
            {tr('Subject:')} <b style={{ color: theme.text }}>{preview.subject}</b>
          </div>
          {/* The real email, exactly as it would arrive. sandbox: no scripts,
              and the email has none anyway. */}
          <iframe title={tr("This week's email")} srcDoc={preview.html} sandbox=""
            style={{ width: '100%', height: 760, border: 'none', background: '#FAF8F4' }} />
        </div>
      ) : null}
    </Card>
  )
}

/**
 * The family's subscription, once sign-up is open. Nothing at all while it is
 * closed, and a plain line for the allow list, who never pay.
 */
function Subscription() {
  const [b, setB] = useState<BillingStatus | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => { api.billing().then(setB).catch(() => undefined) }, [])
  if (!b || !b.signup_open) return null

  const day = (iso: string | null) => iso
    ? new Date(iso).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
  const line = b.comped ? tr('Your family has Word Sprout free.')
    : b.status === 'trialing' ? tr('Free trial — ends {date}.', { date: day(b.trial_end) })
    : b.status === 'active' ? tr('Subscribed — renews {date}.', { date: day(b.current_period_end) })
    : tr('No subscription.')

  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>{tr('Subscription')}</div>
      <p style={{ margin: '0 0 12px', color: theme.text2, fontSize: 15 }}>
        <Trace id="T26.status">{line}</Trace>
      </p>
      {b.can_manage ? (
        <Button tone="quiet" onClick={async () => {
          setProblem(null)
          try { window.location.assign((await api.billingPortal()).url) }
          catch (err) { setProblem(fromServer(err, 'That did not work.')) }
        }}>{tr('Change card or cancel')}</Button>
      ) : null}
      {problem ? <p style={{ margin: '10px 0 0', color: theme.red, fontSize: 14 }}>{problem}</p> : null}
    </Card>
  )
}

/** What is kept and what leaves the app, in plain words (build list item 6,
 *  24 Oct 2026). Facts from research/how-it-works.md section 2. */
function DataHandling() {
  const p = { margin: '0 0 8px', color: theme.muted, fontSize: 15, lineHeight: 1.5 } as const
  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {tr('How your data is handled')}
      </div>
      <p style={p}>
        <b>{tr('What we keep:')}</b>{' '}
        {tr("your sign-in, and for each child a name, school year, picture and every answer they give. It is kept in the app's own database on Google Cloud.")}
      </p>
      <p style={p}>
        <b>{tr('What leaves the app:')}</b>{' '}
        {tr("a word or a book number, when you look one up, and the emails we send you, which include your child's first name and progress. No AI runs while a child practises.")}
      </p>
      <p style={{ ...p, marginBottom: 0 }}>
        {tr('You can delete everything on this page. To see what we keep about your child, email us at hello@wordsprout.net.')}
      </p>
    </Card>
  )
}

/**
 * The switch for the data labels (lib/trace.tsx). Added 8 Oct 2026: the key
 * combination alone was never found, so the feature looked like it did not
 * exist. Here, a grown-up can find it, and a child never sees it.
 */
function DataLabels() {
  const { on, toggle } = useTrace()
  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {tr('Show where data is stored')}
      </div>
      <p style={{ margin: '0 0 12px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
        {tr('Puts a small label on every value, form and answer in the app, naming the table it comes from or is saved to. A dashed label is read from that table; a solid one with an arrow (→) is saved to it. Click any label to open the data map at that table. It stays on, on this device, until you turn it off here.')}
      </p>
      <Button tone={on ? 'danger' : 'quiet'} onClick={toggle}>
        {on ? tr('Turn labels off') : tr('Turn labels on')}
      </Button>
    </Card>
  )
}

function AccountTab({ device, onLanguage }: { device: string | null; onLanguage: () => void }) {
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)

  // A child's tablet, signed in with a device code: the account's own parts
  // (billing, delete, more devices) stay on the grown-up's phone,
  // and the server refuses them here anyway (22 Oct 2026).
  if (device) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Card>
          <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
            {tr("This is a child's tablet: {device}", { device })}

          </div>
          <p style={{ margin: 0, color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
            {tr('It was signed in with a device code, so no Google account is stored on it. Billing, deleting the account and adding tablets are done on your own phone.')}
          </p>
        </Card>
        <ReportEmails />
        <LanguageCard onChanged={onLanguage} />
        <DataHandling />
        <DataLabels />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <DevicesPanel />
      <Subscription />
      <ReportEmails />
      <LanguageCard onChanged={onLanguage} />
      <DataHandling />
      <DataLabels />

      <Card style={{ borderColor: theme.red }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.red, marginBottom: 6 }}>
          {tr('Delete everything')}
        </div>
        <p style={{ margin: '0 0 14px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
          {tr('Removes the account, every child and every answer. This cannot be undone.')}
        </p>
        <input
          style={{ ...inputStyle, marginBottom: 12 }}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={tr('Type DELETE to confirm')}
        />
        <Button
          tone="danger"
          disabled={confirm.trim().toUpperCase() !== 'DELETE' || done}
          onClick={async () => {
            await api.deleteAccount(confirm)
            setDone(true)
            window.location.reload()
          }}
        >
          {tr('Delete everything')}
        </Button>
      </Card>
    </div>
  )
}

function Row({ label, value, trace }: {
  label: string; value: string
  /** Where the number came from, for the provenance overlay. */
  trace: string
}) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '9px 0', borderBottom: `1px solid ${theme.borderSoft}`,
    }}>
      <span style={{ color: theme.muted, fontSize: 15 }}>{label}</span>
      <span style={{ color: theme.text, fontSize: 15, fontWeight: 700 }}>
        <Trace id={trace}>{value}</Trace>
      </span>
    </div>
  )
}

/**
 * The starting-point check (beta), Sam's build list item 10 (29 Oct 2026).
 * StartingCheck.tsx runs it; this is the way in, and the last result.
 */
function StartingCheckCard({ child, onStart }: { child: Child; onStart: () => void }) {
  const [last, setLast] = useState<{ when: string; offset: number | null } | null>(null)
  useEffect(() => {
    api.lastStartingCheck(child.learner_id)
      .then((r) => setLast(r.check?.finished_at
        ? { when: r.check.finished_at, offset: r.check.suggested_offset } : null))
      .catch(() => setLast(null))
  }, [child.learner_id])
  return (
    <div style={{
      margin: '0 0 16px', padding: '10px 12px', borderRadius: radius.sm,
      background: theme.blueFaint, border: `1px solid ${theme.blueTint}`,
      fontSize: 14, color: theme.text, lineHeight: 1.5,
    }}>
      <b>{tr('Starting-point check (beta)')}</b>
      <div>{tr('About 5 minutes. {name} hears 20 words and taps what each one means. It suggests a level; you choose.', { name: child.display_name })}</div>
      {last ? (
        <div style={{ color: theme.muted }}>
          {tr('Last check: {date}', { date: new Date(last.when).toLocaleDateString(dateLocale()) })}
        </div>
      ) : null}
      <div style={{ marginTop: 8 }}>
        <Button tone="quiet" onClick={onStart}>{tr('Start the check')}</Button>
      </div>
    </div>
  )
}

/**
 * A trial kept on this device (TryIt.tsx) before the account existed. Now
 * that consent is given, its words can join a child's list, the missed ones
 * first; only which words, and which were missed, are sent. Or delete it.
 */
function TrialCarryOver({ children }: { children: Child[] }) {
  const [words] = useState(() => tryStore.forCarryOver())
  const [gone, setGone] = useState(!tryStore.has())
  const [who, setWho] = useState(children[0]?.learner_id ?? '')
  const [message, setMessage] = useState<string | null>(null)
  if (gone || !children.length) return null
  const missed = words.filter((w) => w.missed).length
  return (
    <Card style={{ background: theme.blueFaint }}>
      <b>{tr('A trial on this device')}</b>
      <p style={{ margin: '6px 0 10px', fontSize: 14, lineHeight: 1.5, color: theme.text }}>
        {tr("Before the account, this device kept a trial: {n} words, {m} of them missed. Add them to a child's list, the missed ones first?", { n: words.length, m: missed })}
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {children.length > 1 ? (
          <select value={who} onChange={(e) => setWho(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            {children.map((c) => <option key={c.learner_id} value={c.learner_id}>{c.display_name}</option>)}
          </select>
        ) : null}
        <Button tone="quiet" disabled={!words.length} onClick={async () => {
          try {
            const r = await api.fromDeviceTrial(who, words)
            tryStore.clear()
            setMessage(tr('{n} words added. The trial on this device is deleted.', { n: r.added }))
            window.setTimeout(() => setGone(true), 2500)
          } catch (err) {
            setMessage(fromServer(err, 'That did not save.'))
          }
        }}>{tr('Add them')}</Button>
        <Button tone="ghost" onClick={() => { tryStore.clear(); setGone(true) }}>{tr('Delete the trial')}</Button>
      </div>
      {message ? <p style={{ margin: '8px 0 0', fontSize: 14, color: theme.text }}>{message}</p> : null}
    </Card>
  )
}
